import React from 'react';
import useReveal from '../../hooks/useReveal';
import { BAR_ANIMATION_MS, REVEAL_DELAY_MS, REVEAL_DURATION_MS, toMs } from '../../constants/animation';

interface SiteAffinityRendererProps {
  title: string;
  data: any;
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const parseNumber = (v: any): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v.replace(/[^0-9.\-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const BAR_COLOR = '#3B6FC4';
const TRACK_COLOR = '#E5E7EB';

export default function SiteAffinityRenderer({ title, data }: SiteAffinityRendererProps) {
  const { ref, isVisible } = useReveal<HTMLDivElement>({ threshold: 0.25, once: true });
  type Row = Record<string, any>;
  let rows: Row[] = [];

  const candidate = (data as any) || {};
  const possibleArrays: any[] = [];
  if (Array.isArray(candidate)) possibleArrays.push(candidate);
  if (Array.isArray(candidate.rows)) possibleArrays.push(candidate.rows);
  if (Array.isArray(candidate.data)) possibleArrays.push(candidate.data);

  if (possibleArrays.length > 0) {
    const arr = possibleArrays.find(a => Array.isArray(a) && a.length > 0 && typeof a[0] === 'object');
    if (arr) rows = arr as Row[];
  }

  if (rows.length === 0 && data && typeof data === 'object') {
    // Object map: { label: number }
    rows = Object.entries(data).map(([k, v]) => ({ label: k, value: typeof v === 'number' ? v : 0 }));
  }

  if (rows.length === 0) return null;

  // Determine label and numeric fields
  const preferLabelKeys = ['domain name', 'domain', 'site', 'website', 'publisher', 'label', 'name'];
  const preferValueKeys = ['uv index', 'uvindex', 'index', 'uv', 'affinity', 'score', 'qvi audience', 'audience'];

  const normalizeKey = (k: string) => k.toLowerCase().trim();
  const findKey = (keys: string[], obj: Row) => {
    const lookup = Object.keys(obj);
    for (const want of keys) {
      const hit = lookup.find((k) => normalizeKey(k) === want);
      if (hit) return hit;
    }
    return null;
  };

  // Convert rows into items { label, value, aux? }
  const items = rows.map((r) => {
    const labelKey = findKey(preferLabelKeys, r) || Object.keys(r).find((k) => typeof r[k] === 'string') || 'label';
    const valueKey = findKey(preferValueKeys, r) || Object.keys(r).find((k) => typeof r[k] === 'number') || 'value';
    const label = String(r[labelKey] ?? r.label ?? r.name ?? '');
    const value = parseNumber(r[valueKey]);
    const auxKeys = Object.keys(r).filter((k) => k !== labelKey && k !== valueKey);
    const aux: Record<string, any> = {};
    for (const k of auxKeys) aux[k] = r[k];
    return { label, value, aux };
  }).filter((x) => x.label && typeof x.value === 'number');

  if (items.length === 0) return null;

  // Sort by value desc and cap to 12 for readability; filter missing/NaN
  const sorted = items
    .filter(x => typeof x.value === 'number' && Number.isFinite(x.value))
    .sort((a, b) => (b.value as number) - (a.value as number))
    .slice(0, 12);

  if (sorted.length === 0) return null;

  const values = sorted.map((x) => x.value);
  const baseline = 100;
  const maxValue = Math.max(baseline * 1.4, ...values);

  const formatAux = (aux: Record<string, any>): string | null => {
    const entries = Object.entries(aux)
      .filter(([key, val]) => {
        if (val === null || val === undefined) return false;
        if (typeof val === 'object') return false;
        const str = String(val).trim();
        return str.length > 0 && normalizeKey(key) !== 'index' && normalizeKey(key) !== 'value';
      })
      .slice(0, 2);
    if (entries.length === 0) return null;
    return entries
      .map(([key, val]) => {
        const cleanKey = key.replace(/[_-]/g, ' ');
        return `${cleanKey}: ${val}`;
      })
      .join(' • ');
  };

  return (
    <div
      ref={ref}
      className="bg-white rounded-xl shadow-md p-6 space-y-6"
      style={{
        transition: `transform ${toMs(REVEAL_DURATION_MS)} ease-out, opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        opacity: isVisible ? 1 : 0.25,
        transitionDelay: isVisible ? toMs(REVEAL_DELAY_MS) : '0ms',
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-slate-900">{title}</h3>
          <p className="text-slate-600 text-sm">
            BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data
          </p>
        </div>
      </div>
      <div className="space-y-5">
        {sorted.map((item, idx) => {
          const width = clamp((item.value / maxValue) * 100, 0, 100);
          const baselinePos = clamp((baseline / maxValue) * 100, 0, 100);
          const auxSummary = formatAux(item.aux);
          const rowDelay = isVisible ? REVEAL_DELAY_MS + idx * 140 : 0;
          return (
            <div
              key={`${item.label}-${idx}`}
              className="space-y-3"
              style={{
                transition: `opacity ${toMs(REVEAL_DURATION_MS)} ease-out`,
                opacity: isVisible ? 1 : 0,
                transitionDelay: toMs(rowDelay),
              }}
            >
              <div className="flex items-center justify-between text-sm">
                <div className="font-medium text-slate-900">{item.label}</div>
                <div className="font-semibold text-slate-900">{Math.round(item.value)}</div>
              </div>
              <div className="relative">
                <div className="relative h-8 rounded-full" style={{ backgroundColor: TRACK_COLOR }}>
                  <div
                    className="absolute inset-y-0"
                    style={{ left: `${baselinePos}%` }}
                  >
                    <div className="w-[2px] h-full bg-white/85" />
                  </div>
                  <div
                    className="absolute inset-y-0 left-0 rounded-full flex items-center justify-end pr-3 text-xs font-semibold text-white"
                    style={{
                      width: `${isVisible ? width : 0}%`,
                      minWidth: width > 0 ? '48px' : '0',
                      backgroundColor: BAR_COLOR,
                      transition: `width ${toMs(BAR_ANIMATION_MS)} ease-out`,
                      transitionDelay: toMs(rowDelay),
                    }}
                  >
                    {Math.round(item.value)}
                  </div>
                </div>
                <div
                  className="absolute top-full mt-1 text-[11px] font-semibold text-slate-500"
                  style={{ left: `${baselinePos}%`, transform: 'translateX(-50%)' }}
                >
                  100
                </div>
              </div>
              {auxSummary ? (
                <div className="text-xs text-slate-500">{auxSummary}</div>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="text-center text-[11px] text-slate-400 uppercase tracking-wide">
        Online Adult Population
      </div>
    </div>
  );
}
